import { useEffect, useState } from "react";

type Id = number | string | null | undefined;

/** True when someone other than `me` holds an unexpired lease. */
export function isLeaseActive(
  lockedBy: Id,
  lockedUntil: string | null | undefined,
  me: Id,
  now: Date,
): boolean {
  if (lockedBy == null || !lockedUntil) return false;
  if (String(lockedBy) === String(me ?? "")) return false;
  return Date.parse(lockedUntil) > now.getTime();
}

/** isLeaseActive that re-renders once, when the lease expires. */
export function useLeaseActive(
  lockedBy: Id,
  lockedUntil: string | null | undefined,
  me: Id,
  now: () => Date = () => new Date(),
): boolean {
  const [, setTick] = useState(0);
  const active = isLeaseActive(lockedBy, lockedUntil, me, now());
  useEffect(() => {
    if (!active || !lockedUntil) return;
    const ms = Date.parse(lockedUntil) - now().getTime();
    // setTimeout caps at 2^31-1 ms; a longer lease is re-checked at the cap.
    const id = setTimeout(
      () => setTick((n) => n + 1),
      Math.min(ms, 2 ** 31 - 1),
    );
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, lockedUntil]);
  return active;
}
